/**
 * Leave Types View Page — read-only detail view.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { useOne } from '@refinedev/core';
import {
  Button, Card, CardContent, CardHeader, CardTitle, Badge,
} from '@yesbos/ui';
import { ArrowLeft, Edit } from 'lucide-react';

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</dt>
      <dd className="text-sm font-medium">{value ?? '—'}</dd>
    </div>
  );
}

export function LeaveTypesViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useOne({ resource: 'leave_types', id: id! });
  const lt = data?.data as Record<string, unknown> | undefined;

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;
  if (!lt) return <div className="p-6 text-sm text-destructive">Leave type not found.</div>;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/leave-types')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-semibold">{String(lt.name ?? '')}</h1>
          <Badge variant={lt.is_active ? 'default' : 'secondary'}>{lt.is_active ? 'Active' : 'Inactive'}</Badge>
        </div>
        <Button variant="outline" onClick={() => navigate(`/leave-types/${id}/edit`)}>
          <Edit className="h-4 w-4 mr-2" />Edit
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle>Leave Type Details</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Name" value={String(lt.name ?? '')} />
            <Field label="Code" value={String(lt.code ?? '')} />
            <Field label="Annual Quota" value={`${lt.annual_quota ?? 0} days`} />
            <Field label="Max Consecutive Days" value={lt.max_consecutive_days ? `${lt.max_consecutive_days} days` : 'Unlimited'} />
            <Field label="Min Service Days" value={`${lt.min_service_days ?? 0} days`} />
            <Field label="Max Carry Forward" value={`${lt.max_carry_forward ?? 0} days`} />
            <Field label="Applicable Gender" value={String(lt.applicable_gender || 'All')} />
            <Field label="Paid Leave" value={lt.is_paid ? 'Yes' : 'No'} />
            <Field label="Carry Forward" value={lt.is_carry_forward ? 'Yes' : 'No'} />
            <Field label="Encashable" value={lt.is_encashable ? 'Yes' : 'No'} />
          </dl>
          {lt.description && (
            <div className="mt-4 space-y-1">
              <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Description</dt>
              <dd className="text-sm">{String(lt.description)}</dd>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default LeaveTypesViewPage;
